import { create } from "zustand";

import type { Trade } from "../api/types";
import { createBatcher } from "../lib/batch";
import { mergeTrade, tradeKey } from "../lib/trades";

/** Сколько сделок держим. За сутки их проходят сотни тысяч. */
export const TAPE_LIMIT = 1000;

/** Сколько идентификаторов помним ради дедупликации. */
const SEEN_LIMIT = 2000;

interface TapeState {
  trades: Trade[];
  push: (t: Trade) => void;
  clear: () => void;
}

/**
 * Дедупликация по сделке аккаунта (`tradeKey`) — вторым слоем поверх stream_id.
 * Аккаунт в ключе затем, что двое своих бывают мейкерами одной сделки, и у
 * каждого своя доля под тем же id.
 *
 * web_api отдаёт 200 последних событий ленты при КАЖДОМ подключении, а набор
 * stream_id в dispatch ограничен и после долгого разрыва уже не покрывает
 * повтор. Идентификатор сделки детерминирован (веха M2), поэтому по нему
 * переподключение становится по-настоящему идемпотентным.
 *
 * Повтор id при этом ЗАМЕЩАЕТ запись, а не отбрасывается: НАШИ сделки трейдер
 * публикует на каждый переход статуса (MATCHED -> MINED -> CONFIRMED, а также
 * RETRYING/FAILED) под одним id — таков контракт шины (docs/bus-contracts.md).
 * Отбрасывание оставляло бы в ленте первый MATCHED навсегда: сорвавшаяся
 * on-chain сделка выглядела бы состоявшейся, и позицию считали бы по данным,
 * которые биржа уже отозвала.
 */
const seen = new Set<string>();
const seenOrder: string[] = [];

function isDuplicate(t: Trade): boolean {
  if (!t.id) return false;
  const key = tradeKey(t);
  if (seen.has(key)) return true;
  seen.add(key);
  seenOrder.push(key);
  if (seenOrder.length > SEEN_LIMIT) {
    const oldest = seenOrder.shift();
    if (oldest !== undefined) seen.delete(oldest);
  }
  return false;
}

/** Замещает уже показанную сделку новым состоянием того же id. */
function replaceExisting(t: Trade): void {
  const key = tradeKey(t);
  const queued = pending.findIndex((p) => tradeKey(p) === key);
  if (queued >= 0) {
    pending[queued] = t;
    return; // batcher уже взведён той записью, которую мы заместили
  }
  useTape.setState((state) => {
    const i = state.trades.findIndex((x) => tradeKey(x) === key);
    const cur = i >= 0 ? state.trades[i] : undefined;
    if (!cur) return state; // вытеснена лимитом — показывать уже негде
    // Реплей истории при переподключении несёт то же содержимое: без этой
    // отсечки 200 повторов дали бы 200 холостых ре-рендеров ленты.
    //
    // Адрес агрессора в сравнении обязателен: он приезжает ОТДЕЛЬНОЙ публикацией
    // той же сделки и запросто с тем же статусом, размером и ценой — то есть без
    // этого условия единственное, ради чего публикация и делалась, отбрасывалось
    // бы молча.
    if (
      cur.status === t.status &&
      cur.size === t.size &&
      cur.price === t.price &&
      (cur.taker_address ?? "") === (t.taker_address ?? "")
    ) {
      return state;
    }
    const trades = state.trades.slice();
    // Замещение по id, но без потери узнанного: шаг статуса приезжает без адреса
    // агрессора (см. lib/trades.mergeTrade).
    trades[i] = mergeTrade(cur, t);
    return { trades };
  });
}

let pending: Trade[] = [];

const batcher = createBatcher(() => {
  if (pending.length === 0) return;
  const batch = pending;
  pending = [];
  useTape.setState((state) => {
    // Новые сверху; batch пришёл в хронологическом порядке.
    const trades = [...batch.reverse(), ...state.trades];
    if (trades.length > TAPE_LIMIT) trades.length = TAPE_LIMIT;
    return { trades };
  });
});

export const useTape = create<TapeState>(() => ({
  trades: [],

  push: (t) => {
    if (isDuplicate(t)) {
      replaceExisting(t);
      return;
    }
    pending.push(t);
    batcher.schedule();
  },

  clear: () => {
    pending = [];
    seen.clear();
    seenOrder.length = 0;
    useTape.setState({ trades: [] });
  },
}));
